import { getAuth } from 'firebase/auth';

// OpenGraph Service for creating link previews
// This service creates simple, reliable link previews without external API calls

class OpenGraphService {
  constructor() {
    this.cache = new Map();
  }

  // Extract URLs from text content
  extractUrls(text) {
    if (!text || typeof text !== 'string') {
      return [];
    }

    // Matches:
    //  - https://example.com/path
    //  - http://example.com
    //  - www.example.com/path
    //  - example.com/path
    // Avoids emails and @mentions
    const urlPattern =
      /(?:^|\s)(?!@)(?!.*@)[([]?((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[\w\-._~:/?#[\]@!$&'()*+,;=%]*)?)/g;

    const matches = [];
    let match;
    while ((match = urlPattern.exec(text)) !== null) {
      // match[1] is the captured URL without leading space/paren
      matches.push(match[1]);
    }

    const urls = matches || [];

    // Normalize and clean URLs (remove trailing punctuation, ensure protocol)
    const cleanedUrls = urls
      .map((url) => {
        // Remove surrounding brackets/parentheses if present
        let u = url.replace(/^[([\s]*/g, '').replace(/[)\]]+$/g, '');
        // Remove common trailing punctuation
        u = u.replace(/[.,;:!?]+$/g, '');
        // Ensure protocol
        if (!/^https?:\/\//i.test(u)) {
          u = `https://${u}`;
        }
        return u;
      })
      .filter((url) => {
        // Basic URL validation
        try {
          // Disallow emails just in case
          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return false;
          new URL(url);
          return true;
        } catch {
          return false;
        }
      });

    // Deduplicate while preserving order
    const uniqueCleaned = [...new Set(cleanedUrls)];

    return uniqueCleaned;
  }

  // Create link preview data for a single URL
  async fetchOpenGraphData(url) {
    // Check cache first
    if (this.cache.has(url)) {
      const cached = this.cache.get(url);
      if (cached && cached.hasOpenGraph && cached.image) {
        return cached;
      }
      // Allow re-fetch for cached fallbacks since the remote metadata may have changed
      this.cache.delete(url);
    }

    try {
      // Prefer provider-specific enrichment first (client-side, fast)
      const providerData = await this._fetchFromProvider(url);
      if (providerData) {
        this.cache.set(url, providerData);
        return providerData;
      }

      // Next try server-backed OG fetch for richer data (image, title, etc.)
      const og = await this._fetchFromServer(url);
      if (og) {
        const resolvedUrl = this._resolveCanonicalUrl(url, og.url);
        const imageUrl = this._resolveImageUrl(resolvedUrl, og.image);
        const enriched = {
          title: og.title || this._fallbackTitle(url),
          description:
            og.description ||
            `Visit ${new URL(url).hostname.replace('www.', '')}`,
          image: imageUrl || this._fallbackImage(url),
          siteName:
            og.siteName ||
            new URL(resolvedUrl).hostname.replace('www.', '').toUpperCase(),
          url: resolvedUrl,
          originalUrl: url,
          hasOpenGraph: Boolean(og.title || og.description || og.image),
          isClientSide: false,
          isFallback: !imageUrl,
        };
        this.cache.set(url, enriched);
        return enriched;
      }

      // Fallback: simple client-side preview with best-effort image
      const previewData = this._createLinkPreview(url, {
        includeFallbackImage: true,
      });
      this.cache.set(url, previewData);
      return previewData;
    } catch (error) {
      return this._createFallbackData(url);
    }
  }

  async _fetchFromProvider(url) {
    // YouTube enrichment via oEmbed (client-side)
    if (this._isYouTubeUrl(url)) {
      const yt = await this._fetchYouTubeOEmbed(url);
      if (yt) {
        return {
          title: yt.title || this._fallbackTitle(url),
          description: yt.author ? `By ${yt.author}` : '',
          image: yt.thumbnail || this._fallbackImage(url),
          siteName: 'YOUTUBE.COM',
          url,
          originalUrl: url,
          hasOpenGraph: true,
          isClientSide: false,
        };
      }
    }
    return null;
  }

  _isYouTubeUrl(url) {
    try {
      const u = new URL(url);
      const h = u.hostname.toLowerCase();
      return (
        h.includes('youtube.com') ||
        h.includes('youtu.be') ||
        h.includes('music.youtube.com')
      );
    } catch {
      return false;
    }
  }

  async _fetchYouTubeOEmbed(url) {
    try {
      const endpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(
        url
      )}&format=json`;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3500);
      const res = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`oEmbed ${res.status}`);
      const data = await res.json();
      return {
        title: data.title,
        author: data.author_name,
        thumbnail: data.thumbnail_url,
      };
    } catch (e) {
      return null;
    }
  }

  async _fetchFromServer(url) {
    try {
      // Skip server fetch on localhost — the CF doesn't allow localhost origins.
      // Use process.env.NODE_ENV as primary signal (CRA/webpack replaces at build time),
      // and window.location.hostname as a reliable runtime fallback.
      const isDev =
        (typeof process !== 'undefined' &&
          process.env?.NODE_ENV === 'development') ||
        (typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'));
      if (isDev) {
        return null;
      }

      const base =
        (typeof process !== 'undefined' &&
          process.env &&
          process.env.REACT_APP_OG_FUNCTION_URL) ||
        'https://us-central1-ilovethissong-fd021.cloudfunctions.net/fetchOpenGraphData';

      // Get Firebase ID token for authentication
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        return null;
      }
      const idToken = await user.getIdToken();

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4500);
      const res = await fetch(`${base}?url=${encodeURIComponent(url)}`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });
      clearTimeout(timeout);

      // Handle specific error cases
      if (res.status === 403) {
        console.warn(
          `OpenGraph: ${url} blocked (403 Forbidden) - likely bot protection`
        );
        return this._createBlockedDomainFallback(url);
      }

      if (res.status === 429) {
        console.warn(`OpenGraph: ${url} rate limited (429 Too Many Requests)`);
        return this._createRateLimitedFallback(url);
      }

      if (!res.ok) {
        throw new Error(`OG function responded ${res.status}`);
      }

      const data = await res.json();
      // normalize
      return {
        title: data.title || '',
        description: data.description || '',
        image: data.image || '',
        url: data.url || url,
        siteName: data.siteName || '',
      };
    } catch (e) {
      console.warn(`OpenGraph server fetch failed for ${url}:`, e.message);
      return null;
    }
  }

  // Create a simple link preview based on URL
  _createLinkPreview(url, opts = {}) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');
      const pathname = urlObj.pathname;

      // Try to extract meaningful information from the URL
      let title = this._fallbackTitle(url);
      let description = `Visit ${hostname}`;

      // Special handling for common domains
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        title = 'YouTube Video';
        description = 'Watch this video on YouTube';
      } else if (hostname.includes('spotify.com')) {
        title = 'Spotify Track';
        description = 'Listen on Spotify';
      } else if (
        hostname.includes('twitter.com') ||
        hostname.includes('x.com')
      ) {
        title = 'Twitter Post';
        description = 'View on Twitter';
      } else if (hostname.includes('instagram.com')) {
        title = 'Instagram Post';
        description = 'View on Instagram';
      } else if (hostname.includes('reddit.com')) {
        title = 'Reddit Post';
        description = 'View on Reddit';
      } else if (pathname && pathname.length > 1) {
        // Try to extract title from pathname
        const pathParts = pathname.split('/').filter((part) => part.length > 0);
        if (pathParts.length > 0) {
          const lastPart = pathParts[pathParts.length - 1];
          if (lastPart.length > 3) {
            title = decodeURIComponent(lastPart)
              .replace(/[-_]/g, ' ')
              .replace(/\.[^/.]+$/, '') // Remove file extension
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
          }
        }
      }

      return {
        title: title || 'Link Preview',
        description: description || `Visit ${hostname} for more information`,
        image: opts.includeFallbackImage ? this._fallbackImage(url) : null,
        siteName: hostname.toUpperCase(),
        url: url,
        originalUrl: url,
        hasOpenGraph: false,
        isClientSide: true,
        isFallback: true,
      };
    } catch {
      return this._createFallbackData(url);
    }
  }

  _fallbackTitle(url) {
    try {
      const hostname = new URL(url).hostname.replace('www.', '');
      return hostname.charAt(0).toUpperCase() + hostname.slice(1);
    } catch {
      return 'Link Preview';
    }
  }

  _fallbackImage(url) {
    try {
      // YouTube thumbnail
      const yt = this._extractYouTubeId(url);
      if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
      // Use placeholder image for sites without proper OpenGraph images
      return '/assets/LinkPlaceholder.png';
    } catch {
      return '/assets/LinkPlaceholder.png';
    }
  }

  _resolveImageUrl(baseUrl, image) {
    if (!image || typeof image !== 'string') {
      return '';
    }

    const trimmed = image.trim();
    if (!trimmed) {
      return '';
    }

    // Data URLs can be used as-is
    if (/^data:/i.test(trimmed)) {
      return trimmed;
    }

    // Already absolute (http/https)
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    try {
      const base = new URL(baseUrl);

      if (trimmed.startsWith('//')) {
        return `${base.protocol}${trimmed}`;
      }

      return new URL(trimmed, base).href;
    } catch (error) {
      console.warn('OpenGraph: failed to resolve image URL', {
        baseUrl,
        image,
        error: error.message,
      });
      return '';
    }
  }

  _resolveCanonicalUrl(originalUrl, candidateUrl) {
    if (!candidateUrl || typeof candidateUrl !== 'string') {
      return originalUrl;
    }

    const trimmed = candidateUrl.trim();
    if (!trimmed) {
      return originalUrl;
    }

    try {
      if (/^https?:\/\//i.test(trimmed)) {
        return new URL(trimmed).href;
      }
      const original = new URL(originalUrl);
      if (trimmed.startsWith('//')) {
        return `${original.protocol}${trimmed}`;
      }
      return new URL(trimmed, original).href;
    } catch (error) {
      console.warn('OpenGraph: failed to resolve canonical URL', {
        originalUrl,
        candidateUrl,
        error: error.message,
      });
      return originalUrl;
    }
  }

  _extractYouTubeId(url) {
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
      const v = u.searchParams.get('v');
      if (v) return v;
      const m = u.pathname.match(/\/embed\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  }

  // Create fallback data for blocked domains (403 Forbidden)
  _createBlockedDomainFallback(url) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');

      // Special handling for known blocked domains
      let title = hostname.charAt(0).toUpperCase() + hostname.slice(1);
      let description = `Visit ${hostname} for more information`;
      let siteName = hostname.toUpperCase();

      if (hostname.includes('bandcamp.com')) {
        title = 'Bandcamp';
        description = 'Music platform - visit to listen and discover new music';
        siteName = 'BANDCAMP';
      } else if (hostname.includes('soundcloud.com')) {
        title = 'SoundCloud';
        description = 'Music streaming platform - visit to listen to tracks';
        siteName = 'SOUNDCLOUD';
      } else if (hostname.includes('spotify.com')) {
        title = 'Spotify';
        description = 'Music streaming service - visit to listen';
        siteName = 'SPOTIFY';
      }

      return {
        title,
        description,
        image: this._getDomainFallbackImage(hostname),
        siteName,
        url,
        originalUrl: url,
        hasOpenGraph: false,
        isBlocked: true,
        isClientSide: true,
        isFallback: true,
      };
    } catch {
      return this._createFallbackData(url);
    }
  }

  // Create fallback data for rate limited domains (429 Too Many Requests)
  _createRateLimitedFallback(url) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');

      return {
        title: hostname.charAt(0).toUpperCase() + hostname.slice(1),
        description: `Visit ${hostname} for more information (rate limited)`,
        image: this._getDomainFallbackImage(hostname),
        siteName: hostname.toUpperCase(),
        url,
        originalUrl: url,
        hasOpenGraph: false,
        isRateLimited: true,
        isClientSide: true,
        isFallback: true,
      };
    } catch {
      return this._createFallbackData(url);
    }
  }

  // Get appropriate fallback image for domain
  _getDomainFallbackImage(hostname) {
    if (hostname.includes('bandcamp.com')) {
      return '/assets/bandcamp-placeholder.png'; // You can add this image
    } else if (hostname.includes('soundcloud.com')) {
      return '/assets/soundcloud-placeholder.png'; // You can add this image
    } else if (hostname.includes('spotify.com')) {
      return '/assets/spotify-placeholder.png'; // You can add this image
    } else if (
      hostname.includes('youtube.com') ||
      hostname.includes('youtu.be')
    ) {
      return '/assets/youtube-placeholder.png'; // You can add this image
    } else if (hostname.includes('nytimes.com')) {
      return '/assets/nytimes-placeholder.png'; // You can add this image
    } else if (hostname.includes('github.com')) {
      return '/assets/github-placeholder.png'; // You can add this image
    }

    // Default fallback
    return '/assets/LinkPlaceholder.png';
  }

  // Create fallback data when everything fails
  _createFallbackData(url) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');

      return {
        title: hostname.charAt(0).toUpperCase() + hostname.slice(1),
        description: `Visit ${hostname} for more information`,
        image: null,
        siteName: hostname.toUpperCase(),
        url,
        originalUrl: url,
        hasOpenGraph: false,
        isFallback: true,
        isClientSide: true,
      };
    } catch {
      return {
        title: 'Link Preview',
        description: `Visit this link for more information`,
        image: null,
        siteName: 'External Link',
        url,
        originalUrl: url,
        hasOpenGraph: false,
        isFallback: true,
        isClientSide: true,
      };
    }
  }

  // Fetch link previews for multiple URLs in parallel
  async fetchMultipleUrls(urls) {
    const uniqueUrls = [...new Set(urls)];

    // Create all previews in parallel (they're fast since they're client-side)
    const promises = uniqueUrls.map((url) => this.fetchOpenGraphData(url));

    try {
      // All promises should resolve quickly since they're client-side
      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      // Return fallback data for all URLs on error
      return uniqueUrls.map((url) => this._createFallbackData(url));
    }
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get cache size
  getCacheSize() {
    return this.cache.size;
  }
}

// Export singleton instance
export const openGraphService = new OpenGraphService();

// Export utility functions
export const extractUrls = (text) => openGraphService.extractUrls(text);
export const fetchOpenGraphData = (url) =>
  openGraphService.fetchOpenGraphData(url);
export const fetchMultipleUrls = (urls) =>
  openGraphService.fetchMultipleUrls(urls);
